import React, { useState, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { Constants } from "@eagerdog/constants";
import { type ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import { Modal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Confirm from "src/components/Confirm/Confirm";

import { ActionsRenderer } from "./ActionsRenderer";
import styles from "./BannedUsersModal.module.scss";

interface IBan {
  _id: string;
  club_id: string;
  user_id: string;
  reason: string;
  notes?: string;
  banned_by: string;
  banned_at: string;
  expires_at?: string;
  user: {
    _id: string;
    first_name?: string;
    last_name?: string;
    display_name: string;
    email: string;
  };
  banned_by_user?: {
    _id: string;
    display_name: string;
  };
}

interface ISearchUser {
  _id: string;
  first_name?: string;
  last_name?: string;
  display_name: string;
  email: string;
  is_banned: boolean;
}

interface IProps {
  isShown: boolean;
  hide: () => void;
  clubId: string;
}

interface IBanRow {
  _id: string;
  user_id: string;
  name: string;
  email: string;
  reason: string;
  banned_at: string;
  ban: IBan;
}

const banReasonOptions: IOption[] = [
  { id: Constants.ban_reason.non_payment, value: "Unpaid Balance" },
  { id: Constants.ban_reason.code_of_conduct, value: "Code of Conduct Violation" },
  { id: Constants.ban_reason.safety_concern, value: "Safety Concern" },
  { id: Constants.ban_reason.fraudulent_entry, value: "Fraudulent Entry Information" },
  { id: Constants.ban_reason.disruptive_behavior, value: "Disruptive Behavior" },
  { id: Constants.ban_reason.other, value: "Other" },
];

const getReasonLabel = (reason: string): string => {
  const option = banReasonOptions.find(o => o.id === reason);
  return option?.value || reason;
};

const BannedUsersModal: React.FC<IProps> = ({ isShown, hide, clubId }) => {
  const [bans, setBans] = useState<IBan[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showAddBanModal, setShowAddBanModal] = useState<boolean>(false);
  const [showConfirmUnban, setShowConfirmUnban] = useState<boolean>(false);
  const [selectedBan, setSelectedBan] = useState<IBan | null>(null);

  // Add ban form state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<ISearchUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ISearchUser | null>(null);
  const [reason, setReason] = useState<IOption | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const handleRemoveBan = useCallback((ban: IBan) => {
    setSelectedBan(ban);
    setShowConfirmUnban(true);
  }, []);

  const colDefs: ColDef<IBanRow>[] = [
    { field: "name", headerName: "Name", minWidth: 180, flex: 1, filter: "agTextColumnFilter" },
    { field: "email", headerName: "Email", minWidth: 220, flex: 1, filter: "agTextColumnFilter" },
    { field: "reason", headerName: "Reason", minWidth: 180, flex: 1, filter: true },
    { field: "banned_at", headerName: "Banned", minWidth: 120, filter: true },
    {
      headerName: "",
      field: "actions" as any,
      maxWidth: 60,
      minWidth: 60,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        onRemove: handleRemoveBan
      },
      resizable: false,
      sortable: false,
      filter: false,
      cellStyle: {
        "overflow": "visible",
        "zIndex": "2"
      }
    }
  ];

  const loadBans = useCallback(async () => {
    if (!clubId) return;
    
    setLoading(true);
    try {
      const result = await apiService.getClubBans(clubId);
      setBans(result.bans);
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: "Failed to load banned users",
        type: "fail",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    if (isShown && clubId) {
      loadBans();
    }
  }, [isShown, clubId, loadBans]);

  const searchUsers = useCallback(async (term: string) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await apiService.searchUsersForBan(clubId, term);
      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  }, [clubId]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm) {
        searchUsers(searchTerm);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, searchUsers]);

  const handleAddBan = async () => {
    if (!selectedUser || !reason) return;

    if (reason.id === Constants.ban_reason.other && !notes.trim()) {
      toast.show({
        title: "Error",
        content: "Notes are required when selecting 'Other' as the reason",
        type: "fail",
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.banUser(
        clubId,
        selectedUser._id,
        reason.id,
        notes || undefined
      );
      
      toast.show({
        title: "Success",
        content: `${selectedUser.display_name || selectedUser.email} has been banned`,
        type: "success",
        duration: 5000,
      });

      // Reset form and reload
      resetAddBanForm();
      setShowAddBanModal(false);
      loadBans();
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Failed to ban user",
        type: "fail",
        duration: 5000,
        errorDetails: error as AxiosError,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnban = async () => {
    if (!selectedBan) return;

    try {
      await apiService.unbanUser(clubId, selectedBan.user_id);
      
      toast.show({
        title: "Success",
        content: `${selectedBan.user.display_name || selectedBan.user.email} has been unbanned`,
        type: "success",
        duration: 5000,
      });

      setShowConfirmUnban(false);
      setSelectedBan(null);
      loadBans();
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: "Failed to remove ban",
        type: "fail",
        duration: 5000,
        errorDetails: error as AxiosError,
      });
    }
  };

  const resetAddBanForm = () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedUser(null);
    setReason(null);
    setNotes("");
  };

  const getRowData = (): IBanRow[] => {
    return bans.map((ban) => ({
      _id: ban._id,
      user_id: ban.user_id,
      name: ban.user.display_name || `${ban.user.first_name || ""} ${ban.user.last_name || ""}`.trim() || "Unknown",
      email: ban.user.email,
      reason: getReasonLabel(ban.reason),
      banned_at: new Date(ban.banned_at).toLocaleDateString(),
      ban: ban,
    }));
  };

  const renderAddBanModal = () => (
    <div className={styles.addBanModal}>
      <h3>Ban User</h3>
      
      <div className={styles.searchSection}>
        <Input
          label="Search User"
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setSelectedUser(null);
          }}
          placeholder="Search by name or email..."
        />
        
        {isSearching && <div className={styles.searching}>Searching...</div>}
        
        {searchResults.length > 0 && !selectedUser && (
          <div className={styles.searchResults}>
            {searchResults.map((user) => (
              <div
                key={user._id}
                className={`${styles.searchResult} ${user.is_banned ? styles.disabled : ""}`}
                onClick={() => {
                  if (!user.is_banned) {
                    setSelectedUser(user);
                    setSearchResults([]);
                  }
                }}
              >
                <span className={styles.userName}>
                  {user.display_name || `${user.first_name || ""} ${user.last_name || ""}`.trim()}
                </span>
                <span className={styles.userEmail}>{user.email}</span>
                {user.is_banned && <span className={styles.alreadyBanned}>Already Banned</span>}
              </div>
            ))}
          </div>
        )}

        {selectedUser && (
          <div className={styles.selectedUser}>
            <div className={styles.userInfo}>
              <span className={styles.userName}>
                {selectedUser.display_name || `${selectedUser.first_name || ""} ${selectedUser.last_name || ""}`.trim() || "Unknown"}
              </span>
              <span className={styles.userEmail}>{selectedUser.email}</span>
            </div>
            <Button variant="secondary" onClick={() => setSelectedUser(null)}>Clear</Button>
          </div>
        )}
      </div>

      <div className={styles.formField}>
        <Dropdown
          label="Reason"
          value={reason || { id: "", value: "Select a reason..." }}
          options={banReasonOptions}
          onChange={(_, value) => setReason(value)}
          placeholder="Select a reason..."
        />
      </div>

      <div className={styles.formField}>
        <Input
          label={reason?.id === Constants.ban_reason.other ? "Notes (Required)" : "Notes (Optional)"}
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional details..."
        />
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={() => {
            resetAddBanForm();
            setShowAddBanModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddBan}
          disabled={!selectedUser || !reason}
          isLoading={isSubmitting}
        >
          Ban User
        </Button>
      </div>
    </div>
  );

  const renderContent = () => (
    <div className={styles.bannedUsersModal}>
      <div className={styles.header}>
        <h2>Banned Users</h2>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading...</div>
      ) : bans.length === 0 ? (
        <div className={styles.empty}>
          <p>No banned users</p>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <div className={styles.tableWrapInner}>
            <AgGridReact
              rowData={getRowData()}
              columnDefs={colDefs}
              domLayout="autoHeight"
              gridOptions={{
                defaultColDef: {
                  flex: 1,
                  minWidth: 100,
                  resizable: true
                },
                animateRows: true,
                rowHeight: 48,
                headerHeight: 48,
                suppressCellFocus: true,
                overlayNoRowsTemplate: '<span>No banned users</span>'
              }}
            />
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <Button onClick={() => setShowAddBanModal(true)}>+ Ban User</Button>
        <Button onClick={hide}>Close</Button>
      </div>

      <Modal
        className={styles.addBanModalWrapper}
        isShown={showAddBanModal}
        hide={() => {
          resetAddBanForm();
          setShowAddBanModal(false);
        }}
        modalContent={renderAddBanModal()}
      />

      <Modal
        className="Confirmation"
        isShown={showConfirmUnban}
        hide={() => {
          setShowConfirmUnban(false);
          setSelectedBan(null);
        }}
        modalContent={
          <Confirm
            message={
              <p>
                Are you sure you want to remove the ban for{" "}
                <strong>{selectedBan?.user.display_name || selectedBan?.user.email}</strong>?
              </p>
            }
            onConfirm={handleUnban}
            onCancel={() => {
              setShowConfirmUnban(false);
              setSelectedBan(null);
            }}
          />
        }
      />
    </div>
  );

  return (
    <Modal
      className={styles.bannedUsersModalWrapper}
      isShown={isShown}
      hide={hide}
      modalContent={renderContent()}
    />
  );
};

export default BannedUsersModal;
